import type { TimelineItemWithCommentCount } from "@travel/shared";
import type { ItemStatus } from "../../utils/status";
import { formatTime, getTimezoneAbbr } from "../../utils/time";

interface Props {
  item: TimelineItemWithCommentCount;
  status: ItemStatus;
  onClick: () => void;
  onCommentIconClick: () => void;
}

export function TimelineItem({ item, status, onClick, onCommentIconClick }: Props) {
  const tzAbbr = getTimezoneAbbr(new Date(item.startUtc));

  const stateClass =
    status === "past"
      ? "opacity-60"
      : status === "current"
        ? "ring-2 ring-rose-300 bg-rose-50/60 border-rose-200"
        : "";

  return (
    <div
      id={item.id}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={`relative bg-white rounded-2xl border border-slate-200 px-3 py-3 flex gap-3 active:bg-slate-50 transition cursor-pointer ${stateClass}`}
    >
      <div className="flex flex-col items-end shrink-0 w-14 text-right pt-0.5">
        <span className="text-sm font-semibold tabular-nums text-slate-900">
          {formatTime(item.startUtc)}
        </span>
        <span className="text-xs text-slate-400 tabular-nums">{formatTime(item.endUtc)}</span>
        <span className="text-[10px] text-slate-400 mt-0.5">{tzAbbr}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 break-words leading-snug">{item.title}</div>
        {item.description && (
          <div className="text-sm text-slate-600 whitespace-pre-line mt-1 break-words">
            {item.description}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCommentIconClick();
        }}
        className="shrink-0 self-start flex items-center gap-1 text-slate-500 hover:text-slate-900 px-2 py-1 -mt-1 -mr-1 min-w-[36px] min-h-[36px] justify-center rounded-lg hover:bg-slate-100"
        aria-label="코멘트 보기"
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {item.commentCount > 0 && (
          <span className="text-xs font-medium tabular-nums">{item.commentCount}</span>
        )}
      </button>
    </div>
  );
}
